import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth";
import { homeForRole } from "@/api";
import { PrimaryButton } from "@/ui";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate(homeForRole(user.role), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      navigate(homeForRole(u.role), { replace: true });
    } catch (err) {
      toast.error(err.message || "Accesso non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="theme-brand min-h-screen bg-[#12100E] text-[#FAF7F2] flex flex-col">
      <div className="px-6 md:px-10 pt-8">
        <div className="font-display text-xl">YU <span className="text-[#E05A2C]">YieldUp</span></div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md fade-up">
          <div className="eyebrow text-[#E05A2C] mb-5">Area riservata</div>
          <h1 className="font-display text-3xl md:text-4xl leading-tight uppercase">
            La tua struttura. I tuoi numeri. Nero su bianco.
          </h1>
          <p className="mt-4 text-[#C4BBB0] font-light">
            Il portale privato della tua struttura, aggiornato dal tuo specialista.
          </p>

          <form onSubmit={submit} className="mt-9 space-y-4">
            <div>
              <label className="eyebrow text-[#857C72] block mb-2">Email</label>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@struttura.it"
                className="w-full rounded-full bg-[#1C1815] border border-[#2A241F] px-5 py-3 text-[#FAF7F2] placeholder-[#6f675f] outline-none focus:border-[#E05A2C] transition-colors"
                required
              />
            </div>
            <div>
              <label className="eyebrow text-[#857C72] block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Inserisci la tua password"
                  className="w-full rounded-full bg-[#1C1815] border border-[#2A241F] pl-5 pr-12 py-3 text-[#FAF7F2] placeholder-[#6f675f] outline-none focus:border-[#E05A2C] transition-colors"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Nascondi password" : "Mostra password"}
                  title={showPw ? "Nascondi password" : "Mostra password"}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#857C72] hover:text-[#E05A2C] transition-colors"
                >
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div className="pt-3">
              <PrimaryButton type="submit" disabled={busy} className="w-full">
                {busy ? "Accesso in corso…" : "Entra"}
              </PrimaryButton>
            </div>
          </form>

          <p className="mt-8 text-xs text-[#6f675f] font-light">
            Hai ricevuto un invito via email? Apri il link "Scegli la tua password" per impostare l'accesso.
          </p>
        </div>
      </div>
    </div>
  );
}
